import { Component, OnInit } from '@angular/core';
import { GeographicCharts } from '../../../../shared/classes/geographic-chart-options';
import * as Highcharts from 'highcharts';
import { GeographicMapService } from '../../../services/geographic-map.service';
import { ReusableFunctions } from '../../../../shared/classes/reusable-functions';
import {
    ILoanTypePurposeData,
    IYears,
    IMapParameters,
    IDonutChartSeriesData,
    IPieChartInfo,
    IGeographicMapData,
} from '../../../interfaces/geographic-map-data';
import { DisplayOptions } from '../../../../shared/interfaces/display-options';
import { Router } from '@angular/router';
import { IPageHeadline } from '../../../../shared/classes/page-title-parameters';
import { NoDataActionType, NoDataSourceType } from '../../../../shared/interfaces/no-data';
import { DecimalPipe } from '@angular/common';
import { ILargeCardInteriorOptions } from '../../../../shared/interfaces/card-data';

var mapDataFromFile = require('../../../../../../assets/json/us-all.geo.json');

@Component({
    selector: 'app-us-map',
    templateUrl: './historical-insights-geographic.component.html',
    styleUrls: ['./historical-insights-geographic.component.scss'],
})
export class HistoricalInsightGeographicComponent implements OnInit {
    lgCharts: GeographicCharts.GeographicChart;
    seperators = Highcharts.geojson(mapDataFromFile, 'mapline'); // store lines infomration that delineate borders between states
    displayOptions: DisplayOptions[];
    mapData: any[] = Highcharts.geojson(mapDataFromFile);
    pageHeadline: IPageHeadline;
    action: NoDataActionType;
    yearOptions: IYears[] = [];
    largeCardInteriorOptions: ILargeCardInteriorOptions;
    parameters: IMapParameters;
    pieChartsInfo: IPieChartInfo[];

    constructor(
        private _mapService: GeographicMapService,
        private _router: Router,
        private decimalPipe: DecimalPipe,
        private reusableFunctions: ReusableFunctions
    ) {}

    ngOnInit() {
        this.action = NoDataActionType.pageStart;
        this.setDrilldownPointers(this.mapData);
        this.setPagetitle();
        this.lgCharts = new GeographicCharts.GeographicChart(
            this.reusableFunctions,
            this._mapService
        );
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);

        this.largeCardInteriorOptions = {
            action: this.action,
            componentTitle: 'US Map',
            hasData: false,
            isDataReady: false,
            searchTerm: '',
            showTitle: false,
            accessToUrl: '',
            uniqueId: 'historical-geographic-chart',
            minHeight: 750,
        };

        this.parameters = {
            dataSource: '',
            displayAs: '',
            loanPurpose: 'All',
            loanType: 'All',
            occupancyType: 'All',
            year: '',
            geographySelection: 'USA',
            occupancy: 'All',
            hFilterJson: this.reusableFunctions.getHierarchyFilters(),
            type: '',
        };

        let noDataPurposePieType = {
            action: this.action,
            searchTerm: '',
            componentTitle: 'Purpose Mix Chart',
            inputSource: NoDataSourceType.chartCard,
        };

        let noDataTypePieType = {
            action: this.action,
            searchTerm: '',
            componentTitle: 'Product Mix Chart',
            inputSource: NoDataSourceType.chartCard,
        };

        this.pieChartsInfo = [
            {
                id: 'pieChartLoanType',
                hasData: false,
                isDataReady: false,
                noDataType: noDataPurposePieType,
                type: 'Type',
                title: 'Product Mix',
            },
            {
                id: 'pieChartLoanPurpose',
                hasData: false,
                isDataReady: false,
                noDataType: noDataTypePieType,
                type: 'Purpose',
                title: 'Purpose Mix',
            },
        ];

        this.setDisplayOptionsSelected();
        this.setUpMainData();
    }

    setUpMainData() {
        this.yearOptions = [];
        if (this.parameters.dataSource === 'HMDA') {
            this._mapService.getAvailableHMDAYearsUpTo5().subscribe((years) => {
                years.forEach((year, index) => {
                    this.yearOptions.push({
                        year: year,
                        index: index,
                        color: '',
                        active: false,
                    });
                });
                if (this.yearOptions.length == 0) {
                    throw new Error('No HMDA years found.');
                }
                this.setInitialYear();
                this.loadCharts();
            });
        } else {
            this.yearOptions = JSON.parse(localStorage.getItem('historical-year-options'));
            this.setInitialYear();
            this.loadCharts();
        }
    }

    setInitialYear() {
        let selectedYear: number;
        if (this.yearOptions && this.yearOptions.length > 0) {
            this.yearOptions.sort((a, b) => a.year - b.year);
            selectedYear = this.yearOptions[this.yearOptions.length - 1]?.year;
        } else {
            selectedYear = new Date().getUTCFullYear() - 1;
        }
        this.parameters.year = selectedYear.toString();
    }

    setMapChartData(geoData: any[], data: IGeographicMapData[], geographySelection = 'USA') {
        geoData.forEach((record) => {
            try {
                let totalUnits = 0;
                let totalVolume = 0;
                var matchingElementArray: any[];
                if (geographySelection === 'USA') {
                    if (this.parameters.dataSource == 'HMDA') {
                        matchingElementArray = data.filter(
                            (hmdaRecord) => hmdaRecord.name.indexOf(record.name) > -1
                        );
                    } else {
                        matchingElementArray = data.filter(
                            (hmdaRecord) => hmdaRecord.name.indexOf(record.properties['hc-a2']) > -1
                        );
                    }
                } else {
                    matchingElementArray = data.filter(
                        (dataRecord) => dataRecord.stateCountyCode === record.properties.fips
                    );
                }

                if (matchingElementArray.length == 0) {
                    record.value = 0;
                    record.properties.units = 0;
                    record.properties.volume = '$' + this.reusableFunctions.formatLargeNumber(0);
                    record.properties.growth = 0;
                    record.properties.loanTypeArray = [];
                    record.properties.loanPurposeArray = [];
                    record.properties.occupancyTypeArray = [];
                }
                record.properties.growth = 0;

                let typeArray = matchingElementArray.filter(
                    (hmdaRecord) => hmdaRecord.type.indexOf('LoanType') > -1
                );
                record.properties.loanTypeArray = [];
                typeArray.forEach((elem) => {
                    totalUnits += elem.units;
                    totalVolume += elem.volume;
                    record.properties.loanTypeArray.push({
                        abbr: elem.abbr,
                        volume: elem.volume,
                        units: elem.units,
                        volumePercent: elem.volumePercent,
                        unitPercent: elem.unitPercent,
                        color: this.reusableFunctions.getLoanTypeColor(elem.abbr),
                    });
                });

                record.properties.units = this.decimalPipe.transform(totalUnits, '1.0-0');
                record.properties.volume =
                    '$' + this.reusableFunctions.formatLargeNumber(totalVolume);

                if (this.parameters.displayAs.toLowerCase() == 'units') {
                    record['value'] = totalUnits;
                } else {
                    record['value'] = totalVolume;
                }

                let purposeArray = matchingElementArray.filter(
                    (hmdaRecord) => hmdaRecord.type.indexOf('Purpose') > -1
                );
                record.properties.loanPurposeArray = [];
                purposeArray.forEach((elem) => {
                    record.properties.loanPurposeArray.push({
                        abbr: elem.abbr,
                        volume: elem.volume,
                        units: elem.units,
                        volumePercent: elem.volumePercent,
                        unitPercent: elem.unitPercent,
                        color: this.reusableFunctions.getPurposeColor(elem.abbr),
                    });
                });

                let occupancyArray = matchingElementArray.filter(
                    (hmdaRecord) => hmdaRecord.type.indexOf('Occupancy') > -1
                );
                record.properties.occupancyTypeArray = [];
                occupancyArray.forEach((elem) => {
                    record.properties.occupancyTypeArray.push({
                        abbr: elem.abbr,
                        volume: elem.volume,
                        units: elem.units,
                        volumePercent: elem.volumePercent,
                        unitPercent: elem.unitPercent,
                        color: '#000000',
                    });
                });
            } catch {
                record.value = 0;
                record.properties.units = 0;
                record.properties.volume = '$0';
                record.properties.growth = 0;
                record.properties.loanTypeArray = [];
                record.properties.loanPurposeArray = [];
                record.properties.occupancyTypeArray = [];
            }
        });
    }

    getMapData() {
        this.largeCardInteriorOptions.hasData = false;
        this.largeCardInteriorOptions.isDataReady = false;
        this.largeCardInteriorOptions.chart = null;
        this.largeCardInteriorOptions = Object.assign({}, this.largeCardInteriorOptions);
        this._mapService.getStateMapData(this.parameters).subscribe(
            (res) => {
                if (res.length > 0) {
                    this.setMapChartData(this.mapData, res);
                    this.largeCardInteriorOptions.chart = Highcharts.mapChart(
                        this.largeCardInteriorOptions.uniqueId,
                        this.lgCharts.getMapOptions(
                            this.mapData,
                            this.seperators,
                            this.parameters.displayAs,
                            this
                        )
                    );
                    this.largeCardInteriorOptions.hasData = true;
                    this.largeCardInteriorOptions.isDataReady = true;
                } else {
                    this.largeCardInteriorOptions.hasData = false;
                    this.largeCardInteriorOptions.isDataReady = true;
                }
                this.largeCardInteriorOptions = Object.assign({}, this.largeCardInteriorOptions);
            },
            (error) => {
                this.largeCardInteriorOptions.action = NoDataActionType.error;
                this.largeCardInteriorOptions.hasData = false;
                this.largeCardInteriorOptions.isDataReady = true;
                this.largeCardInteriorOptions = Object.assign({}, this.largeCardInteriorOptions);
            }
        );
    }

    getPieChartData() {
        this.pieChartsInfo.forEach((pieChart) => {
            pieChart.hasData = false;
            pieChart.isDataReady = false;
            this.parameters.type = pieChart.type;
            this._mapService.getLoanTypePurposeData(this.parameters).subscribe(
                (res) => {
                    if (res.length > 0) {
                        pieChart.hasData = true;
                        pieChart.isDataReady = true;
                        let seriesData = this.createSeriesData(res, pieChart.type);
                        this.updateDonutCharts(
                            pieChart.type,
                            pieChart.title,
                            seriesData,
                            seriesData.reduce((sum, point) => sum + point.y, 0)
                        );
                    } else {
                        pieChart.noDataType.action = this.action;
                        pieChart.hasData = false;
                        pieChart.isDataReady = true;
                    }
                },
                (error) => {
                    pieChart.hasData = false;
                    pieChart.isDataReady = true;
                    pieChart.noDataType.action = NoDataActionType.error;
                }
            );
        });
    }

    loadCharts() {
        this.getMapData();
        this.getPieChartData();
    }

    setPagetitle() {
        this.pageHeadline = {
            module: 'historical-trends',
            moduleIcon: 'fa-chart-line',
            pageTitle: 'Geographical Production',
            linkArray: [
                {
                    moduleLink: 'historical-trends',
                    linkTitle: 'Historical Trends Home',
                    showLink: true,
                    isHome: true,
                },
                {
                    moduleLink: 'historical-trends/insights',
                    linkTitle: 'Historical Trends Insights',
                    showLink: true,
                },
            ],
        };
    }

    yearChange(year: number) {
        if (this.yearOptions.length > 1) {
            this.parameters.year = year.toString();
            this.parameters.geographySelection = 'USA';
            this.loadCharts();
        }
    }

    applyNewFilters(event: string) {
        this.parameters.hFilterJson = event;
        this.action = NoDataActionType.filter;
        this.parameters.geographySelection = 'USA';
        this.loadCharts();
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.action = NoDataActionType.filter;
        this.parameters.geographySelection = 'USA';
        this.setDisplayOptionsSelected();
        this.setUpMainData();
    }

    setDisplayOptionsSelected(): void {
        this.parameters.dataSource = this.displayOptions?.find(
            (item) => item.optionKey === 'dataSource'
        )?.selectedOptions;
        this.parameters.displayAs = this.displayOptions?.find(
            (x) => x.optionKey == 'displayAsHmda'
        )?.selectedOptions;
    }

    updateDonutCharts(
        type: string,
        chartTitle: string,
        seriesData: IDonutChartSeriesData[],
        total: number
    ): void {
        if (type == 'Purpose') {
            new Highcharts.Chart(
                'pieChartLoanPurpose',
                this.lgCharts.createDonutChart(
                    chartTitle,
                    seriesData,
                    this.formatNum(total),
                    this.getDisplayAsOption().toUpperCase()
                )
            );
        } else {
            new Highcharts.Chart(
                'pieChartLoanType',
                this.lgCharts.createDonutChart(
                    chartTitle,
                    seriesData,
                    this.formatNum(total),
                    this.getDisplayAsOption().toUpperCase()
                )
            );
        }
    }

    getDisplayAsOption(): string {
        return this.parameters.displayAs ?? 'Volume';
    }

    setDrilldownPointers(mapData: any[]) {
        mapData.forEach((record) => {
            record.properties.drilldown = record.properties['hc-key'];
            record.drilldown = record.properties['hc-key'];
        });
    }

    createSeriesData(loanData: ILoanTypePurposeData[], type: string): IDonutChartSeriesData[] {
        let seriesData: IDonutChartSeriesData[] = [];
        loanData.forEach((record) => {
            seriesData.push({
                name: record.abbr,
                y: record[this.getDisplayAsOption().toLowerCase()],
                color:
                    type == 'Purpose'
                        ? this.reusableFunctions.getPurposeColor(record.abbr)
                        : this.reusableFunctions.getLoanTypeColor(record.abbr),
            });
        });
        return seriesData;
    }

    formatNum(num: number): string {
        if (this.getDisplayAsOption().toLowerCase() == 'volume') {
            return '$' + this.reusableFunctions.formatLargeNumber(num);
        } else {
            return this.reusableFunctions.formatLargeNumber(num);
        }
    }
}
